import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import {
  useGetUserListImportHistoryQuery,
  useLazyDownloadCustomerImportQuery,
} from 'api/userLists'
import { CustomerImport } from 'api/userLists/importHistory'
import Badge from 'components/Badge'
import HeaderCell from 'components/HeaderCell'
import Spinner from 'components/Spinner'
import TooltipLabel from 'components/TooltipLabel'
import { buildTranslate } from 'locales'
import React from 'react'
import { Table } from 'react-bootstrap-five'
import { merchantTheme } from 'theme'
import ImportRowLimitNote from './ImportRowLimitNote'

const t = buildTranslate('user_lists.import_history')

type DisplayStatus = 'uploading' | 'processing' | 'completed' | 'failed'

function statusBadgeColors(status: DisplayStatus): {
  color: string
  backgroundColor: string
} {
  switch (status) {
    case 'completed':
      return {
        color: merchantTheme.colors.nephritis50,
        backgroundColor: merchantTheme.colors.nephritis10,
      }
    case 'uploading':
    case 'processing':
      return {
        color: merchantTheme.colors.belizeHole50,
        backgroundColor: merchantTheme.colors.belizeHole10,
      }
    case 'failed':
      return {
        color: merchantTheme.colors.cayenne50,
        backgroundColor: merchantTheme.colors.cayenne10,
      }
  }
}

// Maps the server-side (state, snowflake_synced_at, error) triple onto the
// three merchant-facing happy-path states plus Failed (DATA-5058). The UI
// waits for `snowflake_synced_at` before flipping to Completed because
// imported customers aren't queryable in the list until Fivetran replicates
// and RowImport::SnowflakeSyncJob (hourly) marks the upload synced.
function displayStatus(entry: CustomerImport): DisplayStatus {
  if (entry.error) return 'failed'

  switch (entry.state) {
    case 'errored':
    case 'failed':
      return 'failed'
    case 'created':
    case 'processing':
      return 'uploading'
    case 'finalized':
    case 'processed':
      return entry.snowflake_synced_at ? 'completed' : 'processing'
    default:
      return entry.snowflake_synced_at ? 'completed' : 'processing'
  }
}

// Renders `imported / failed` using the server-side row-import counters.
// Both fields exist on every CustomerImport record (legacy + post-migration),
// so no null-fallback branch is needed.
function rowsDisplay(entry: CustomerImport): string {
  const imported = entry.row_imports_completed_count ?? 0
  const failed = entry.row_imports_failed_count ?? 0
  return `${imported.toLocaleString()} / ${failed.toLocaleString()}`
}

type Props = {
  listId: number
}

const ImportHistoryTab: React.FC<Props> = ({ listId }) => {
  const [css] = useStyletron()
  const { data, isLoading } = useGetUserListImportHistoryQuery({
    userListId: listId,
  })
  const [triggerDownload] = useLazyDownloadCustomerImportQuery()

  const handleDownload = async (urlPath: string, filename: string) => {
    const result = await triggerDownload(urlPath)
    if (result.data) {
      const link = document.createElement('a')
      link.href = result.data
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(result.data)
    }
  }

  const entries = data?.data || []

  const statusTooltipSections = [
    { content: t('status_tooltip.intro') },
    { content: t('status_tooltip.uploading') },
    { content: t('status_tooltip.processing') },
    { content: t('status_tooltip.completed') },
  ]

  return (
    <div className="container py-l">
      <ImportRowLimitNote className="mb-m" />
      <Spinner isLoading={isLoading} className="text-center py-xl">
        {entries.length === 0 ? (
          <div className="text-center py-xl">
            <Text.BodyText3 color="grey50" tag="p" className="m-0 font-italic">
              {t('empty')}
            </Text.BodyText3>
          </div>
        ) : (
          <>
            <Table responsive className="mb-0">
              <thead>
                <tr>
                  <HeaderCell>{t('columns.file_name')}</HeaderCell>
                  <HeaderCell>{t('columns.rows')}</HeaderCell>
                  <HeaderCell>{t('columns.date_uploaded')}</HeaderCell>
                  <HeaderCell>
                    <TooltipLabel
                      label={t('columns.status')}
                      sections={statusTooltipSections}
                    />
                  </HeaderCell>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => {
                  const status = displayStatus(entry)
                  const { color, backgroundColor } = statusBadgeColors(status)
                  const uploadedDate = new Date(
                    entry.created_at
                  ).toLocaleDateString()
                  const displayName = entry.filename
                    ? `${entry.filename} — ${uploadedDate}`
                    : '—'
                  // Hoist into a const so TS narrows `string | null` to `string`
                  // inside the conditional render below — avoids an `as string` cast.
                  const errorsUrlPath = entry.errors_url_path

                  // Extracted handlers (qlty: anchor-is-valid, jbarson: extract
                  // complex onClick). Buttons styled as links via Bootstrap's
                  // btn-link so they remain semantically buttons (a11y) while
                  // looking like the inline filename links in the design.
                  const onMainDownload = () =>
                    handleDownload(
                      entry.url_path,
                      entry.filename || 'import.csv'
                    )
                  const onErrorsDownload = () =>
                    handleDownload(
                      errorsUrlPath as string,
                      entry.errors_filename || 'import-errors.csv'
                    )

                  return (
                    <tr key={entry.id}>
                      <td className="body-text-4 py-s px-m">
                        {entry.url_path ? (
                          <>
                            <div>
                              <Text.BodyText4 tag="span" color="grey70">
                                {t('upload_label')}{' '}
                              </Text.BodyText4>
                              <button
                                type="button"
                                className="btn btn-link p-0 border-0 align-baseline text-decoration-underline"
                                onClick={onMainDownload}
                              >
                                <Text.BodyText4 tag="span" color="belizeHole50">
                                  {entry.filename ||
                                    entry.url_path.split('/').pop() ||
                                    entry.url_path}
                                </Text.BodyText4>
                              </button>
                            </div>
                            {errorsUrlPath ? (
                              <div className="mt-xxs">
                                <Text.BodyText5 tag="span" color="grey70">
                                  {t('errors_label')}{' '}
                                </Text.BodyText5>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 border-0 align-baseline text-decoration-underline"
                                  onClick={onErrorsDownload}
                                  aria-label={t('errors_csv_link_aria', {
                                    filename: entry.filename || 'import',
                                  })}
                                >
                                  <Text.BodyText5
                                    tag="span"
                                    color="belizeHole50"
                                  >
                                    {entry.errors_filename || 'errors.csv'}
                                  </Text.BodyText5>
                                </button>
                              </div>
                            ) : null}
                          </>
                        ) : (
                          displayName
                        )}
                      </td>
                      <td className="body-text-4 py-s px-m">
                        {rowsDisplay(entry)}
                      </td>
                      <td className="body-text-4 py-s px-m">{uploadedDate}</td>
                      <td className="body-text-4 py-s px-m">
                        <Badge
                          className={css({ color, backgroundColor })}
                          value={t(`status.${status}`)}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </>
        )}
      </Spinner>
    </div>
  )
}

export default ImportHistoryTab
